import React from 'react';
import _ from 'lodash';
import { ApplicationState } from 'store/rootReducer';
import {
  applyMockServiceTemplate,
  createTemplateFromServiceThunk,
  deleteMockServiceItemThunk,
  deployMockService,
  getLocationsThunk,
  getMockServiceListThunk,
  isReconfigureBlazeDataDialogShown,
  mergeDraft,
  mockServiceChanged,
  mockServicePatchAction,
  mockServiceTemplateChanged,
  openUpgradeMockModalAction,
  reconfigureMockServiceThunk,
  showLoading,
  stopMockServiceThunk,
  toggleExpandedMockServiceRow,
  toggleMockServiceInAction,
  updateMockServicesListCount,
} from 'store/mockServices/mockServiceList/mockServiceListActions';
import { withTranslation, WithTranslation } from 'react-i18next';
import { showToastNotification } from 'store/ui/toasts/toastsActions';
import { MockService, MockServiceCapabilities, SV, TRANSACTIONAL, UpdateConfig } from 'types/rest/mockServiceTypes';
import styles from 'views/MockServices/Tabs/MockServices/MockServicesListBlazeTable.module.css';
import Locations from 'views/MockServices/Tabs/MockServices/Locations';
import { getShortString } from 'utils/miscUtil';
import ServicePicker from 'components/ui/servicePicker/ServicePicker';
import SimpleLoader from 'components/loaders/SimpleLoader';
import { confirm } from 'components/ui/modal/BlazeConfirmationModal';
import BlazeTable from 'components/blazeTable/BlazeTable';
import { BlazeTableColDef, BlazeTableOptions } from 'components/blazeTable/BlazeTable.types';
import { connect } from 'react-redux';
import AssignTemplateModal from './AssignTemplateModal';
import {
  getLocationName,
  getLocValue,
  getMockServiceEndpoint,
  isAppliedTemplateDifferent,
  isLocationScalable,
  isOplLocation,
} from 'utils/mockServiceUtils';
import { isPrivateCloud, isPrivateDataEnabled } from 'utils/bzmUserConfigUtil';
import { MockServiceTemplate } from 'store/mockServiceTemplates/mockServiceTemplateTypes';
import MockServiceNameCell from './Cells/MockServiceNameCell';
import {
  resetMockServiceDetail,
  resetTransactionShuttle,
  setAssetBundle,
} from 'store/mockServices/mockServiceDetail/mockServiceDetailActions';
import { applySearchTermFilterToListWithTags } from 'utils/filterUtils';
import DraftIcon from 'components/ui/icons/DraftIcon';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { openAnalyticsTabItemThunk } from 'store/analytics/ui/analyticsUiActions';
import MockServiceIcon from 'components/icons/MockServiceIcon';
import { Service } from 'types/rest/assetCatalogTypes';
import store from 'store/configureStore';
import MixpanelService from 'utils/mixpanelService';
import { CHANGE_MOCK_SERVICE_LOCATION, OPEN_ANALYTICS_FROM_MS_SCREEN } from 'utils/mixpanelEvents';
import { setPageFilter } from 'store/ui/pageFilters/pageFilterActions';
import UpdateServiceMockModal from './Modals/UpdateServiceMockModal';
import { DevOptions, loadDevOptions } from 'utils/localStorageUtil';
import { hideBackdrop, showBackdrop } from 'store/ui/backdrop/backdropActions';
import MockServiceWarningsModal from './MockServiceWarningsModal';
import { deleteAssetBySslMode } from 'utils/AssetBundleUtils';
import { SSlConfigMode } from './SslConfiguration';
import i18n from 'i18next';
import Endpoint from '../../../../components/endPoint/Endpoint';
import Status from '../../../../components/statusIndicator/Status';
import HeaderCell from './Cells/HeaderCell';
import StatusBadge from '../../../../components/statusIndicator/StatusBadge';
import BootingScreen from '../../MockServices/DeployAnimation/VirtualServiceBootingScreen/BootingScreen';
import Badges from '../../../../components/statusIndicator/Badges';
import MockServiceDetail from './MockServiceDetail/MockServiceDetail';
import MsActionButtons from './Cells/MsActionButtons';
import { formatNumberWithSpaces, getNumberWithTierSuffix } from '../../../../utils/stringUtils';
import SimpleTagList from '../../../../components/ui/tags/SimpleTagList';
import { getMockServiceTemplates } from '../../../../store/mockServiceTemplates/mockServiceTemplatesActions';

type MockServicesListBlazeTableProps = {
  onSelectionChange: (selectedItems: Array<MockService>) => void;
  onRef?: any;
};

type AllProps = StateProps & DispatchProps & WithTranslation & RouteComponentProps & MockServicesListBlazeTableProps;

interface LocalState {
  assignTemplateModalOpen: boolean;
  /** Current MS & Action & Capabilities are used by modal dialogs */
  currentMockService: MockService;
  currentAction: MockServiceAction;
  currentMockServiceCapabilities: MockServiceCapabilities;
  selectCredsModalOpen: boolean;
  isMockServiceUpdateModalOpen: boolean;
  /** This is help property used to lock "go to virtual service" functionality. If set to false redirect has not happen. */
  redirectFinished: boolean;
  upgradeMockModalOpen: boolean;
  filteredTemplates: MockServiceTemplate[];
}

type MockServiceAction = 'DEPLOY' | 'START' | 'STOP' | 'UPDATE' | 'RECONFIGURE' | null;

export const getStatusCell = (rowData: MockService) => {
  if (rowData.metaData.inProgress) {
    return (
      <span id={`mockServiceStatusValue` + rowData.id}>
        <span
          className="badge badge-warning badge-pill align-middle"
          title={i18n.t('mockServices:mockServicesTable.statusMsg.pleaseWait')}
        >
          <SimpleLoader
            loaderSize={15}
            style={{ display: 'inline-block', marginRight: '4px' }}
            showLoadingText={false}
          />
          {'Configuring'}
        </span>
      </span>
    );
  } else if (
    rowData.metaData.isDeployed ||
    rowData.metaData.isFailed ||
    rowData.metaData.isStopped ||
    rowData.isNew === false
  ) {
    return <Status status={rowData.status as any} id={rowData.id} />;
  } else {
    return <>-</>;
  }
};

class MockServicesListBlazeTable extends React.Component<AllProps, LocalState> {
  tableRef: any = React.createRef<BlazeTable>();
  devOptions: DevOptions | undefined = loadDevOptions();
  state = {
    assignTemplateModalOpen: false,
    currentMockService: {} as MockService,
    currentAction: null,
    currentMockServiceCapabilities: {
      isBlazeData: false,
      appliedTemplate: false,
    },
    selectCredsModalOpen: false,
    isMockServiceUpdateModalOpen: false,
    redirectFinished: false,
    upgradeMockModalOpen: false,
    filteredTemplates: [],
  };

  toggleAssignTemplateOpen = (assign: boolean) => {
    this.setState({
      assignTemplateModalOpen: assign,
    });
  };

  upgradeMockModalOpen = () => {
    this.setState(prevState => ({
      upgradeMockModalOpen: !prevState.upgradeMockModalOpen,
    }));
  };

  componentDidMount() {
    //Reset txn shuttle on mount (SPAR-2641)
    this.props.resetTransactionShuttle();

    this.props.showLoading();
    if (!this.props.locData[0]) {
      this.props.getLoc();
    }
    if (this.props.onRef) {
      this.props.onRef(this);
    }
  }

  getTable = (): BlazeTable => {
    return this.tableRef as BlazeTable;
  };

  componentDidUpdate(prevProps: Readonly<AllProps>) {
    const newMs = this.props.dataList.find((ms: MockService) => ms.metaData.isNewest);
    if (newMs && this.props.expandedMockServiceId !== newMs.id) {
      //Expand the newest virtual service draft
      this.goToFirstPage();
      this.props.toggleExpandedMockServiceRow(newMs.id);

      //Change the newest flag, otherwise it will be always open
      const updatedNewMs = { ...newMs };
      updatedNewMs.metaData.isNewest = false;
      this.props.mockServiceChanged(updatedNewMs);
    }

    // If load time stamp changed, list is not same, unselect items
    if (this.props.loadTimeStamp !== prevProps.loadTimeStamp) {
      this.unSelectAll();
    }
    // Do the focus on the virtual service (by id in the url)
    if (this.state.redirectFinished === false && prevProps.dataList !== this.props.dataList) {
      const mockServiceId = (this.props.match.params as any).mockServiceId;
      if (mockServiceId !== undefined) {
        this.redirectToMockService(mockServiceId);
      }
    }
  }

  /**
   * focus on the virtual service (by id in the url)
   */
  redirectToMockService = async (mockServiceId: any) => {
    //Reset filters before redirect is triggered
    this.props.setPageFilter({
      searchTerm: '',
      serviceId: 0,
    });

    // Set lock, so we will not do redirect on next update
    this.setState({ redirectFinished: true }, async () => {
      //Fetch the list with new data after filter reset
      await this.props.getMockServiceListThunk();

      if (mockServiceId !== undefined) {
        // Parse to number otherwise comparisons will not work
        mockServiceId = parseInt(mockServiceId);

        const appState = store.getState() as ApplicationState;
        if (appState.mockservices.dataList.find(sm => sm.id === mockServiceId) === undefined) {
          const draftFound = appState.mockservices.dataList.find(sm => sm.serviceMockId === mockServiceId);
          if (draftFound !== undefined) {
            mockServiceId = draftFound.id; //assign the draft Id, to select the MS correctly.
          }
        }

        this.goToMockService(mockServiceId);

        // Expand the element, if needed
        if (this.props.expandedMockServiceId !== mockServiceId) {
          this.props.toggleExpandedMockServiceRow(mockServiceId);
        }

        // Scroll to the element in the browser
        setTimeout(() => {
          if (window.location.href.toString().indexOf('#mockServices-' + mockServiceId) === -1) {
            window.location.href += '#mockServices-' + mockServiceId;
          }
          this.props.hideBackdrop();
        }, 10);
      }
    });
  };

  goToMockService = (mockServiceId: any) => {
    if (this.tableRef) {
      this.tableRef.goToPageWithSelectedItem(mockServiceId);
    }
  };

  goToFirstPage = () => {
    if (this.tableRef) {
      this.tableRef.pageSelectedHandler(1);
    }
  };

  EndpointCell = (mockService: MockService) => {
    return (
      <Endpoint
        minWidth={220}
        mockService={mockService}
        isRowEnabled={this.isCellEditable(mockService)}
        isPrivateCloudReadOnly={this.props.isPrivateCloudReadOnly}
        endpointPreferenceChanged={this.endpointPreferenceChanged}
        toggleExpandDetail={this.toggleExpandDetail}
        preferredPortChanged={this.preferredPortChanged}
        type={'mockService'}
      />
    );
  };

  endpointPreferenceChanged = (rowData: MockService, newPreference: string) => {
    const mockService = { ...rowData, endpointPreference: newPreference };
    this.props.mockServicePatchAction(mockService);
  };

  locationChanged = (rowData: MockService, location: any) => {
    const switchingFromOpl = isOplLocation(rowData.harborId, rowData.shipId);
    const mockService = { ...rowData, harborId: location.harborId, shipId: location.shipId };

    // If we are changing from OPL location to public cloud,
    // make sure to reset SSL auth in virtual service (it is not visible from UI)
    if (switchingFromOpl) {
      mockService.assets = deleteAssetBySslMode(mockService.assets, SSlConfigMode.SERVER);
      mockService.configKeyAssets = deleteAssetBySslMode(mockService.configKeyAssets, SSlConfigMode.SERVER);
    }

    // If location we have chosen is not scalable, we should set num of instances to 1
    const isScalable: boolean = isLocationScalable(location.harborId, location.shipId);
    if (!isScalable) {
      mockService.replicas = 1;
    }

    MixpanelService.track(CHANGE_MOCK_SERVICE_LOCATION(rowData.status));

    this.props.mockServicePatchAction(mockService);
  };

  LocationCell = (rowData: MockService) => {
    const isRowEnabled = this.isCellEditable(rowData);
    if (isRowEnabled) {
      if (rowData.type === SV) {
        return <span>{'-'}</span>;
      } else {
        return (
          <div id={`mockServiceLocationSelector` + rowData.id}>
            <Locations
              onChangeLocation={location => this.locationChanged(rowData, location)}
              manualPreLoad={true}
              rowData={rowData}
              locationPickerState={this.props.isPrivateCloudReadOnly}
            />
          </div>
        );
      }
    } else {
      return this._displayTextualLocationString(rowData);
    }
  };

  _displayTextualLocationString = (rowData: MockService) => {
    if (rowData.type === SV) {
      let currentSvLocValue: string | undefined = '';

      // DevTest Virtual Service
      if (!_.isNil(rowData.vseId)) {
        currentSvLocValue = '-';
      }
      // VS Virtual Service
      else if (!_.isNil(rowData.shipId)) {
        currentSvLocValue = getLocationName(rowData.harborId, rowData.shipId);
      }

      return (
        <span title={currentSvLocValue} id={`mockServiceLocationValue` + rowData.id}>
          {currentSvLocValue}
        </span>
      );
    } else {
      const locationString = getLocValue(
        rowData.harborId,
        rowData.shipId,
        this.props.locData,
        isPrivateDataEnabled(this.props.blazeUserConfig),
      );

      return (
        <span title={locationString} id={`mockServiceLocationValue` + rowData.id}>
          {locationString}
        </span>
      );
    }
  };

  ServiceCell = (rowData: MockService) => {
    //option drop-down if adding new item
    if (this.isRowEnabled(rowData)) {
      return (
        <div id={`mockServiceServiceSelector` + rowData.id}>
          <ServicePicker
            isCreateEnabled={true}
            isSelectAll={false}
            manualPreLoad={true}
            selectionDisabled={this.props.isPrivateCloudReadOnly}
            preSelected={rowData.serviceId}
            selectHandler={vs => this.serviceChanged(rowData, vs)}
          />
        </div>
      );
    } else {
      return (
        <div title={rowData.serviceName} id={`mockServiceServiceValue` + rowData.id}>
          {getShortString(rowData.serviceName, 30)}
        </div>
      );
    }
  };

  serviceChanged = async (rowData: MockService, vs: Service | any) => {
    // Do not continue with action if service did not really change
    if (rowData.serviceId === vs.id) {
      return;
    }

    const serviceMock = { ...rowData, serviceId: vs.id, serviceName: vs.name };
    if (serviceMock.type === 'TRANSACTIONAL') {
      serviceMock.mockServiceTransactions = [];
    } else if (serviceMock.type === 'SV') {
      serviceMock.assetId = null;
    }

    await this.props.mockServicePatchAction(serviceMock);
  };

  TypeCell = (rowData: MockService) => {
    const iconSize = 15;
    const icons = (
      <span>
        {rowData.httpRunnerEnabled && <MockServiceIcon className={'ml-3'} size={iconSize} type={'HTTP'} />}
        {rowData.messagingRunnerEnabled && <MockServiceIcon className={'ml-3'} size={iconSize} type={'MESSAGING'} />}
      </span>
    );
    const draftIcon = (
      <DraftIcon title="This Virtual Service contains local changes. Save or update to make changes available to others." />
    );
    return (
      <div className={styles.centered}>
        {rowData.isDraft === true && draftIcon}
        {icons}
      </div>
    );
  };

  toggleExpandDetail = async (id: number | null) => {
    this.props.toggleExpandedMockServiceRow(id);

    //Reset detail to initial state before we open detail
    if (id !== null) {
      this.props.resetMockServiceDetail();
    }
  };

  openShowMockServiceWarningModal = async (mockService: MockService) => {
    if (mockService) {
      this.setState({
        currentMockService: mockService,
      });
    }
    this.props.openUpgradeMockModalAction();
  };

  nameChanged = (rowData: MockService, name: string) => {
    const mockService: MockService = { ...rowData, name: name };
    this.props.mockServicePatchAction(mockService);
  };

  preferredPortChanged = (rowData: MockService, preferredPort: string) => {
    const metadata = { preferredPort: preferredPort };
    const mockService: MockService = { ...rowData, metadata };
    this.props.mockServicePatchAction(mockService);
  };

  StatusCell = (rowData: MockService) => {
    const { t } = this.props;
    if (rowData.metaData.inProgress) {
      return (
        <span id={`mockServiceStatusValue` + rowData.id}>
          <span
            className="badge badge-warning badge-pill align-middle"
            title={t('mockServicesTable.statusMsg.pleaseWait')}
          >
            <SimpleLoader
              loaderSize={15}
              style={{ display: 'inline-block', marginRight: '4px' }}
              showLoadingText={false}
            />
            {_.get(rowData, 'rowData.metaData.trackingStatus', 'Configuring') + '...'}
          </span>
        </span>
      );
    } else if (
      rowData.metaData.isDeployed ||
      rowData.metaData.isFailed ||
      rowData.metaData.isStopped ||
      rowData.isNew === false
    ) {
      let titleCase = '';
      switch (rowData.status) {
        case 'FAILED':
          titleCase = 'Failed';
          break;
        case 'RUNNING':
          titleCase = 'Running';
          break;
        case 'STOPPED':
          titleCase = 'Stopped';
          break;
      }

      return (
        <div id={`mockServiceStatus` + rowData.id}>
          {<StatusBadge variant={rowData.status as any} text={titleCase}></StatusBadge>}
        </div>
      );
    } else {
      return <>-</>;
    }
  };

  deployMockService = async (mockService: MockService) => {
    this.props.deployMockService(mockService);
  };

  stopMockService = (mockService: MockService) => {
    this.props.stopMockService(mockService);
  };

  updateMockServiceReconfigurationHandler = async (reconfigureConfig: UpdateConfig) => {
    //Hide modal
    this.setState({ isMockServiceUpdateModalOpen: false }, () => {
      //Update the virtual service
      this.updateMockService(this.state.currentMockService, reconfigureConfig);
    });
  };

  /**
   * If virtual service has either template applied or contains blaze data, we need
   * to ask user if he wants to update template or regenerate artifacts.
   * @param mockService
   */
  updateMockServiceHandler = async (mockService: MockService) => {
    //Show in progress indicator
    await this.props.toggleMockServiceInAction(mockService.id, { inProgressAction: 'MERGE' });

    //Check if virtual service has some blaze data templates
    const isPrivateCloudEnabled = isPrivateCloud(this.props.blazeUserConfig);
    const isTemplateApplied: boolean = await isAppliedTemplateDifferent(mockService);

    // Its worth checking only if service is deployed and PC is switched off
    const isDeployedWithBlazeData =
      !isPrivateCloudEnabled &&
      mockService.status === 'RUNNING' &&
      !mockService.isNew &&
      (await isReconfigureBlazeDataDialogShown(mockService));

    // Disable indicator
    this.props.toggleMockServiceInAction(mockService.id, { inProgressAction: undefined });

    if (isDeployedWithBlazeData || isTemplateApplied) {
      //Do not continue, just show modal
      await this.setState({
        isMockServiceUpdateModalOpen: true,
        currentMockService: mockService,
        currentMockServiceCapabilities: {
          appliedTemplate: isTemplateApplied,
          isBlazeData: isDeployedWithBlazeData,
        },
      });
      return;
    }

    //Continue with update action
    this.updateMockService(mockService);
  };

  updateMockService = async (mockService: MockService, updateConfig?: UpdateConfig) => {
    if (mockService.isDraft === true) {
      this.props.mergeDraft(mockService, null, updateConfig);
    } else {
      this.props.reconfigureMockService(mockService, null, updateConfig);
    }
  };

  /**
   * Used for virtual service name, location and port. Enabled when virtual service is brand new or it is stopped / failed.
   * Disabled for running & configuring state.
   * @param rowData
   */
  isCellEditable = (rowData: MockService) => {
    return (
      (rowData.isNew && !rowData.metaData.inProgress) ||
      (rowData.status !== 'RUNNING' && !rowData.metaData.inProgress && this.props.expandedMockServiceId === rowData.id)
    );
  };

  deleteMockService = (mockService: MockService) => {
    const { t } = this.props;
    const modalBody = (
      <p>
        {t('mockServicesTable.deleteConfirmation')} <b className="text-break">{mockService.name}</b>?
      </p>
    );

    confirm({
      confirmation: modalBody,
      title: t('mockServicesTable.deleteMockService'),
      yesOptionTitle: t('common:actions.delete'),
    }).then(() => {
      // No need to guard this action, just call it directly
      this.props.deleteMockServiceItem(mockService);
    });
  };

  /** Client side filtering of Virtual Services */
  getFilteredMockServices = () => {
    let mockServices = _.cloneDeep(this.props.dataList);

    //Filter by name
    if (!_.isEmpty(this.props.filter.search)) {
      mockServices = applySearchTermFilterToListWithTags(mockServices, this.props.filter.search);
    }

    //Filter by type if there is any defined
    const filteredTypes = this.props.filter.types;
    if (!_.isNil(filteredTypes) && _.size(filteredTypes) !== 0) {
      mockServices = _.filter(mockServices, item => {
        //MOB-44065 //const isHttp = item.httpRunnerEnabled && filteredTypes.includes('HTTP');
        const isHttp = !item.messagingRunnerEnabled && filteredTypes.includes('HTTP');
        const isMessaging = item.messagingRunnerEnabled && filteredTypes.includes('MESSAGING');
        return isHttp || isMessaging;
      });
    }

    //Update total count
    this.props.updateMockServicesListCount(mockServices && mockServices.length ? mockServices.length : 0);

    // Inject a flat `endpoint` string so BlazeTable can sort by accessor 'endpoint'.
    // Both old services (httpEndpoint/httpsEndpoint) and new services (endpoints array)
    // are handled by getMockServiceEndpoint.
    return mockServices.map(ms => ({ ...ms, endpoint: getMockServiceEndpoint(ms) }));
  };

  onAssignTemplate = async (template: MockServiceTemplate) => {
    const mockService = this.state.currentMockService;

    //Apply template
    await this.props.applyMockServiceTemplate(mockService, template);

    //Close modal
    this.toggleAssignTemplateOpen(false);
  };

  openAssignMockServiceTemplateModal = async (mockService: MockService) => {
    // First ensure templates are loaded
    await this.props.getMockServiceTemplates(mockService.serviceId, true);
    const filteredTemplates = this.props.templates.filter(
      (t: MockServiceTemplate) => t.serviceId === mockService.serviceId,
    );
    this.setState({
      currentMockService: mockService,
      filteredTemplates: filteredTemplates,
    });
    this.toggleAssignTemplateOpen(true);
  };

  ActionButtonsCell = (rowData: MockService) => (
    <MsActionButtons
      mockService={rowData}
      isCompact={true}
      isBlazeVseFeatureEnabled={false}
      stopMockService={this.stopMockService}
      createTemplateFromServiceMock={this.createTemplateFromServiceMock}
      isPrivateCloudReadOnly={this.props.isPrivateCloudReadOnly}
      deleteMockService={() => this.deleteMockService(rowData)}
      openAssignMockServiceTemplateModal={() => this.openAssignMockServiceTemplateModal(rowData)}
      deployMockService={() => this.deployMockService(rowData)}
      openAnalytics={async () => {
        const mockServiceId = rowData.isDraft ? rowData.serviceMockId : rowData.id;
        await this.props.showBackdrop('Opening Analytics...');
        MixpanelService.track(OPEN_ANALYTICS_FROM_MS_SCREEN(mockServiceId, rowData.type), true);
        await this.props.history.push('/analytics');
        this.props.openAnalyticsTabItemThunk(mockServiceId);
      }}
    />
  );

  createTemplateFromServiceMock = (rowData: MockService) => {
    this.props.createTemplateFromService(rowData);
  };

  ExpandDetailComponent = (rowData: MockService) => {
    return (
      <div className="p-4">
        {rowData.metaData.inProgress && rowData.metaData.inProgressAction === 'DEPLOY' && (
          <BootingScreen mockService={rowData} />
        )}
        <MockServiceDetail
          saveServiceHandler={this.updateMockServiceHandler}
          rowData={rowData}
          disabled={rowData.metaData.inProgress || this.props.isPrivateCloudReadOnly || rowData.type === 'SV'}
        />
      </div>
    );
  };

  getColsDefinition = () => {
    // Definition of table columns
    const colsDefinition: BlazeTableColDef[] = [
      {
        header: '',
        width: 80,
        cellComponent: (rowData: any) => this.TypeCell(rowData),
        accessor: 'type',
      },
      {
        header: this.props.t('mockServicesTable.columnNames.mockServiceName'),
        width: 240,
        cellComponent: (rowData: MockService) => (
          <MockServiceNameCell
            isEditable={this.isCellEditable(rowData)}
            toggleExpandDetail={this.toggleExpandDetail}
            mockService={rowData}
            onChange={this.nameChanged}
            disabled={this.props.isPrivateCloudReadOnly}
            openMockServiceWarningModal={this.openShowMockServiceWarningModal}
          />
        ),
        accessor: 'name',
        cellClass: 'flex-table-col-overflow-visible',
      },
      {
        header: this.props.t('mockServicesTable.columnNames.service'),
        headerComponent: (
          <HeaderCell
            title={this.props.t('mockServicesTable.columnNames.service')}
            elevioHelpId={'Service_MockService_table'}
          />
        ),
        width: 140,
        cellComponent: (rowData: any) => this.ServiceCell(rowData),
        cellClass: (rowData: any) =>
          !rowData.metaData.isDeployed &&
          !rowData.metaData.isFailed &&
          !rowData.metaData.inProgress &&
          !rowData.metaData.isStopped
            ? 'has-flex-table-dropdown'
            : '',
        accessor: 'serviceName',
      },
      {
        header: this.props.t('mockServicesTable.columnNames.location'),
        width: 140,
        cellComponent: (rowData: any) => this.LocationCell(rowData),
        cellClass: (rowData: any) => (this.isRowEnabled(rowData) ? 'has-flex-table-dropdown' : ''),
        accessor: 'harborId',
      },
      {
        header: this.props.t('mockServicesTable.columnNames.endpoint'),
        headerComponent: (
          <HeaderCell
            title={this.props.t('mockServicesTable.columnNames.endpoint')}
            elevioHelpId={'creating-a-mock-service-help'}
          />
        ),
        cellComponent: (rowData: any) => this.EndpointCell(rowData),
        cellClass: (rowData: any) =>
          rowData.metaData.isDeployed ||
          rowData.metaData.isFailed ||
          rowData.metaData.inProgress ||
          rowData.metaData.isStopped
            ? ''
            : 'has-flex-table-dropdown',
        accessor: 'endpoint',
      },
      {
        header: 'Badges',
        accessor: 'badges',
        width: 190,
        cellComponent: (rowData: any) => {
          return (
            <div className="picked">
              {rowData != null && rowData.tags != null && <SimpleTagList tags={_.sortBy(rowData.tags)} />}
              {!_.isNil(rowData) && rowData.type === TRANSACTIONAL && <Badges mockService={rowData} />}
            </div>
          );
        },
      },
      {
        header: this.props.t('mockServicesTable.columnNames.hits'),
        accessor: 'hits',
        width: 50,
        cellComponent: (rowData: any) => {
          const isHitsAvailable = rowData.hits && rowData.hits !== 0 && rowData.hits !== '-';
          const title = isHitsAvailable ? formatNumberWithSpaces(rowData.hits) + ' Hits' : '-';
          const userFriendlyNumber = isHitsAvailable ? getNumberWithTierSuffix(rowData.hits) : '-';
          return (
            <div className="picked">
              <span title={title}>{userFriendlyNumber}</span>
            </div>
          );
        },
      },
      {
        header: this.props.t('mockServicesTable.columnNames.status'),
        width: 250,
        cellComponent: (rowData: any) => getStatusCell(rowData),
        accessor: 'status',
      },
      {
        header: this.props.t('mockServiceTemplatesTable.columnNames.actions'),
        cellComponent: (rowData: any) => this.ActionButtonsCell(rowData),
        cellClass: 'flex-table-col-overflow-visible',
        accessor: '',
        width: 160,
        isSortable: false,
      },
    ];

    // If dev mode, show virtual service Ids
    if (!_.isNil(this.devOptions) && this.devOptions.devMode) {
      colsDefinition.splice(1, 0, {
        header: 'ID',
        width: 80,
        cellComponent: (rowData: any) => (rowData.isDraft ? rowData.serviceMockId : rowData.id),
        accessor: 'id',
      });
    }

    return colsDefinition;
  };

  tableOptions: BlazeTableOptions = {
    expandDetailComponent: (rowData: any) => this.ExpandDetailComponent(rowData),
    showRowExpander: true,
    columnDef: this.getColsDefinition(),
    pageable: true,
    selectable: true,
    sortable: true,
    pageSize: 10,
    rowPadding: 8,
    customRowClassName: 'blazeTableRow',
    tableName: 'mockServices',
    noResultsText: this.props.t('mockServicesTable.noMockServiceAvailable'),
    minWidth: 1400,
    disabled: this.props.isPrivateCloudReadOnly || false,
  };

  /**
   * Checks whether virtual service row should be enabled for update.
   * @param mockService
   */
  isRowEnabled = (mockService: MockService) => {
    if (mockService.metaData.inProgress || mockService.type === 'SV') {
      return false;
    }

    const isRunningDraft =
      mockService.isDraft === true &&
      !mockService.metaData.isDeployed &&
      !mockService.metaData.isFailed &&
      !mockService.metaData.isStopped;

    const isNewAndNotInProgress = mockService.isNew === true && !mockService.metaData.inProgress;

    return isRunningDraft || isNewAndNotInProgress;
  };

  unSelectAll = (): any => {
    this.getTable().deSelectAll();
  };

  render() {
    return (
      <>
        <AssignTemplateModal
          onAssignTemplate={this.onAssignTemplate}
          onCancel={() => {
            this.toggleAssignTemplateOpen(false);
          }}
          show={this.state.assignTemplateModalOpen}
          templates={this.state.filteredTemplates}
          assignedTemplateId={this.state.currentMockService.appliedTemplateId}
        />

        <MockServiceWarningsModal
          show={this.props.modelOpen}
          key={this.state.currentMockService.id}
          mockService={this.state.currentMockService}
        />

        {this.state.isMockServiceUpdateModalOpen && (
          <UpdateServiceMockModal
            key={this.state.currentMockService.id}
            updateAction={this.updateMockServiceReconfigurationHandler}
            mockServiceCapabilities={this.state.currentMockServiceCapabilities}
            templateId={this.state.currentMockService.appliedTemplateId}
          />
        )}
        <BlazeTable
          onExpandedItemChange={this.toggleExpandDetail}
          data={this.getFilteredMockServices()}
          options={this.tableOptions}
          isLoading={this.props.isLoading}
          expandedItem={this.props.expandedMockServiceId}
          onRef={(ref: any) => (this.tableRef = ref)}
          onSelectionChange={this.props.onSelectionChange}
          showPageSizeDropdown={true}
          pageSizeOptions={[5, 10, 25]}
          resolveRowKey={(item: MockService): number =>
            item.serviceMockId !== undefined ? item.serviceMockId : item.id
          }
        />
      </>
    );
  }
}

const mapStateToProps = ({ mockservices, blazeUserConfig, ui, mockServiceTemplates }: ApplicationState) => ({
  // Virtual Services
  dataList: mockservices.dataList,
  msListCount: mockservices.msListCount,
  serviceList: mockservices.serviceList,
  filterText: mockservices.filterText,
  locData: mockservices.locData,
  isLoading: mockservices.isLoading,
  activePage: mockservices.activePage,
  expandedMockServiceId: mockservices.expandedMockServiceId,
  modelOpen: mockservices.isUpgradeMockModalOpen,
  loadTimeStamp: mockservices.loadTimeStamp,
  filter: mockservices.filter,

  // Templates
  templates: mockServiceTemplates.mockServiceTemplates,

  // UserConfig
  blazeUserConfig: blazeUserConfig.blazeUserConfig,
  isPrivateCloudReadOnly: blazeUserConfig.isPrivateCloudReadOnly,

  // UI
  activeVirtualService: ui.pageFilter.serviceId,
});

const mapDispatchToProps = {
  deleteMockServiceItem: deleteMockServiceItemThunk,
  mockServiceChanged,
  mockServicePatchAction,
  openUpgradeMockModalAction,
  deployMockService,
  updateMockServicesListCount,
  stopMockService: stopMockServiceThunk,
  getLoc: getLocationsThunk,
  showLoading,
  showToastNotification,
  createTemplateFromService: createTemplateFromServiceThunk,
  mockServiceTemplateChanged,
  mergeDraft,
  toggleExpandedMockServiceRow,
  applyMockServiceTemplate,
  resetTransactionShuttle,
  openAnalyticsTabItemThunk, // Opens analytics tab with virtual service
  reconfigureMockService: reconfigureMockServiceThunk,
  setPageFilter,
  getMockServiceListThunk,
  toggleMockServiceInAction,
  showBackdrop,
  hideBackdrop,
  setCertificates: setAssetBundle,
  resetMockServiceDetail,
  getMockServiceTemplates,
};

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = typeof mapDispatchToProps;

export default withRouter(
  withTranslation('mockServices')(connect(mapStateToProps, mapDispatchToProps)(MockServicesListBlazeTable)),
);
